import React from 'react';

interface PhotoGridProps {
  images: string[];
  onImageClick: (index: number) => void;
}

const PhotoGrid = ({ images, onImageClick }: PhotoGridProps) => {
  const count = images.length;
  const MAX_DISPLAY = 4;
  const visibleImages = images.slice(0, MAX_DISPLAY);
  const remainingCount = count - MAX_DISPLAY;

  // 1. Dynamic Container Height
  // If it's a single image, we might want to respect its natural aspect ratio (optional),
  // but for consistency with your "Fixed Layout" request, we keep h-80 or h-96.
  let containerClass = "grid gap-1 w-full rounded-md overflow-hidden h-96"; 

  if (visibleImages.length === 1) {
    containerClass += " grid-cols-1";
  } else if (visibleImages.length === 2) {
    containerClass += " grid-cols-2";
  } else if (visibleImages.length === 3) {
    containerClass += " grid-cols-2 grid-rows-2";
  } else {
    containerClass += " grid-cols-2 grid-rows-2";
  }

  return (
    <div className={containerClass}>
      {visibleImages.map((imgSrc, index) => {
        let spanClasses = "";
        
        // Logic for 3 items: First item spans 2 rows
        if (visibleImages.length === 3 && index === 0) {
          spanClasses = "row-span-2";
        }

        const isLastDisplayed = index === MAX_DISPLAY - 1;
        const showOverlay = isLastDisplayed && remainingCount > 0;

        return (
          <div
            key={index}
            className={`relative w-full h-full cursor-pointer group overflow-hidden bg-gray-100 ${spanClasses}`}
            onClick={() => onImageClick(index)}
          >
            {/* LAYER 1: The Blurred Background 
                This fills the entire cell using object-cover so there are no white gaps.
                We blur it heavily to create a nice backdrop. */}
            <img
              src={imgSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50"
              aria-hidden="true"
            />

            {/* LAYER 2: The Main Image
                We use 'object-contain' so the FULL image is always visible.
                It sits on top of the blurred version. */}
            <img
              src={imgSrc}
              alt={`Gallery ${index}`}
              className="relative w-full h-full object-contain transition-transform duration-300 group-hover:scale-105 z-10"
              loading="lazy"
            />
            
            {/* Hover Darken Effect */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 z-20" />

            {/* The +X Overlay */}
            {showOverlay && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[2px] z-30">
                <span className="text-white text-3xl font-bold">
                  +{remainingCount}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PhotoGrid;